import { useState } from 'react'
import { getLogs, clearLogs, type LogEntry } from '../api'
import { useAuth } from '../layout/AuthContext'
import { useServerList } from '../hooks/useServerList'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ConfirmDialog from '../components/ConfirmDialog'
import { LOG_VIEW_PAGE_SIZE, LOG_LEVEL_COLORS, LOG_LEVEL_FALLBACK_COLOR, LOG_BADGE_TEXT_COLOR } from '../../shared/constants'
import { fill, PAGES_LOGS_VIEWER_TEXTS as T } from '../../shared/texts'

// Mirrors server utils/logModules.ts (labels for the filter dropdown).
const LEVELS = ['fatal', 'error', 'warn', 'info', 'debug']
const MODULES: { key: string, label: string }[] = [
    { key: 'system', label: T.modules.system },
    { key: 'auth', label: T.modules.auth },
    { key: 'webhook', label: T.modules.webhook },
    { key: 'stripe', label: T.modules.stripe },
    { key: 'paypal', label: T.modules.paypal },
    { key: 'billing', label: T.modules.billing },
    { key: 'ingest', label: T.modules.ingest },
    { key: 'export', label: T.modules.export },
    { key: 'mail', label: T.modules.mail },
    { key: 'scheduler', label: T.modules.scheduler },
]
const MODULE_LABEL: Record<string, string> = Object.fromEntries(MODULES.map((m) => [m.key, m.label]))

export default function LogsViewer({ initialQuery }: { initialQuery?: string } = {}) {
    const { can } = useAuth()
    const canClear = can('logs', 'delete')

    const [module, setModule] = useState('')
    const [level, setLevel] = useState('')
    const [version, setVersion] = useState(0) // bump to reload (refresh / after clear)

    // Opened from a transaction ("Log-Einträge zu dieser Transaktion") → preselect its id.
    const list = useServerList<LogEntry>(getLogs, version, {
        initialQ: initialQuery,
        sort: 'timestamp', dir: 'desc', pageSize: LOG_VIEW_PAGE_SIZE,
        params: { module: module || undefined, level: level || undefined },
    })

    const [busy, setBusy] = useState(false)
    const [confirmClear, setConfirmClear] = useState(false)
    const [clearError, setClearError] = useState('')
    async function doClear() {
        setBusy(true); setClearError('')
        try { await clearLogs(); setConfirmClear(false); setVersion((v) => v + 1) }
        catch (e: any) { setClearError(e?.response?.data?.error ?? e?.message ?? T.deleteFailed) }
        finally { setBusy(false) }
    }

    const columns: Column<LogEntry>[] = [
        {
            key: 'timestamp', header: T.columns.time, defaultWidth: 170,
            render: (r) => new Date(r.timestamp).toLocaleString('de-DE'),
        },
        {
            key: 'level', header: T.columns.level, className: 'center', defaultWidth: 80,
            render: (r) => (
                <span className="badge" style={{ background: LOG_LEVEL_COLORS[r.level] ?? LOG_LEVEL_FALLBACK_COLOR, color: LOG_BADGE_TEXT_COLOR }}>{r.level}</span>
            ),
        },
        {
            key: 'module', header: T.columns.module, defaultWidth: 130,
            render: (r) => MODULE_LABEL[r.module] ?? r.module,
        },
        {
            key: 'message', header: T.columns.message, defaultWidth: 600,
            render: (r) => <span style={{ whiteSpace: 'pre-wrap' }}>{r.message}</span>,
        },
    ]

    const filtered = list.q.trim() !== '' || module !== '' || level !== ''

    return (
        <div className="list-page">
            <ListToolbar
                title={T.title}
                infoText={T.info}
                action={<>
                    <button type="button" onClick={() => setVersion((v) => v + 1)} disabled={list.loading}>
                        {list.loading ? '…' : T.refresh}
                    </button>
                    {canClear && <button type="button" onClick={() => setConfirmClear(true)} disabled={busy}>{T.clearLogs}</button>}
                </>}
            />

            {list.error && <div className="notice err">{list.error}</div>}

            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={filtered}
                search={{ value: list.q, onChange: list.setQ }}
                filters={<>
                    <label>
                        {T.moduleFilter}
                        <select value={module} onChange={(e) => setModule(e.target.value)}>
                            <option value="">{T.all}</option>
                            {MODULES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                        </select>
                    </label>
                    <label>
                        {T.levelFilter}
                        <select value={level} onChange={(e) => setLevel(e.target.value)}>
                            <option value="">{T.levelAll}</option>
                            {LEVELS.map((l) => <option key={l} value={l}>{fill(T.levelFrom, { level: l })}</option>)}
                        </select>
                    </label>
                </>}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">{T.noneFound}</p>
                ) : (
                    <DataTable
                        tableId="logs"
                        columns={columns}
                        rows={list.rows}
                        rowKey={(r) => r._id}
                    />
                )}
            </ListPanel>

            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={list.setPageSize} />

            <ConfirmDialog
                open={confirmClear}
                title={T.clearLogs}
                message={T.clearQuestion}
                confirmLabel={T.delete}
                danger
                busy={busy}
                error={clearError}
                onConfirm={doClear}
                onCancel={() => { if (!busy) { setConfirmClear(false); setClearError('') } }}
            />
        </div>
    )
}
